import { useState, useEffect, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'
import type { Paginated, PaginatedQuery } from '@/types'

export interface QRCode {
  trolley_id: number
  trolley_name: Nullable<string>
  trolley_location: Nullable<string>
  qr_code_key: Nullable<string>
  qr_code_url: Nullable<string>
  has_qr_code: Nullable<boolean>
  generated_at: Nullable<string>
  [key: string]: unknown
}

type UseGetQRCodeReturn = Paginated<QRCode> & {
  isLoading: boolean
  error: Nullable<string>
  refetch: () => Promise<void>
}

export function useGetQRCode(params?: PaginatedQuery): UseGetQRCodeReturn {
  const [qrCodes, setQrCodes] = useState<QRCode[]>([])
  const [pagination, setPagination] = useState({
    page: params?.page ?? 1,
    page_size: params?.page_size ?? 12,
    total: 0,
    total_pages: 0,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const fetchQRCodes = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const search = params?.search?.trim()
      const response = await apiClient.get<Paginated<QRCode>>('/qr-codes/', {
        params: {
          page: params?.page ?? 1,
          page_size: params?.page_size ?? 12,
          ...(search ? { search } : {}),
        },
      })
      setQrCodes(response.data.items)
      setPagination({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total,
        total_pages: response.data.total_pages,
      })
    } catch (err) {
      setError(handleErrorRef.current(err))
      setQrCodes([])
      setPagination({
        page: params?.page ?? 1,
        page_size: params?.page_size ?? 12,
        total: 0,
        total_pages: 0,
      })
    } finally {
      setIsLoading(false)
    }
  }, [params?.page, params?.page_size, params?.search])

  useEffect(() => {
    fetchQRCodes()
  }, [fetchQRCodes])

  return {
    items: qrCodes,
    page: pagination.page,
    page_size: pagination.page_size,
    total: pagination.total,
    total_pages: pagination.total_pages,
    isLoading,
    error,
    refetch: fetchQRCodes,
  }
}
